<template>
  <div class="orders-page">
    <h2>All Orders & Journeys</h2>
    
    <div class="stats-summary">
      <div class="stat-box">
        <h4>{{ stats.totalOrders }}</h4>
        <p>Total Orders</p>
      </div>
      <div class="stat-box">
        <h4>{{ stats.inProgressJourneys }}</h4>
        <p>In Progress</p>
      </div>
      <div class="stat-box">
        <h4>{{ stats.completedOrders }}</h4>
        <p>Completed</p>
      </div>
      <div class="stat-box">
        <h4>{{ stats.paidOrders }}</h4>
        <p>Paid Orders</p>
      </div>
    </div>
    
    <div class="toolbar">
      <input v-model="search" placeholder="Filter by email or name" />
      <select v-model="statusFilter" class="filter-select">
        <option value="all">All Statuses</option>
        <option value="paid">Paid Orders</option>
        <option value="unpaid">Unpaid Orders</option>
        <option value="in_progress">In Progress Journeys</option>
        <option value="finished">Finished (Not Ordered)</option>
      </select>
      <button @click="fetchAllData" :disabled="loading">Refresh</button>
    </div>
    <div v-if="loading">Loading orders...</div>
    <div v-else>
      <table class="orders-table">
        <thead>
          <tr>
            <th>ID / Journey</th>
            <th>User</th>
            <th>Product</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Total</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in filtered" :key="o.id" :class="getRowClass(o)">
            <td>
              <code class="id-code">{{ o.id.substring(0, 12) }}...</code>
              <span v-if="o.type === 'journey'" class="type-badge journey">Journey</span>
              <span v-else class="type-badge order">Order</span>
            </td>
            <td>{{ o.userEmail || o.accountEmail || o.emailAddress || 'N/A' }}</td>
            <td>{{ o.productName || o.name || 'Multiple Items' }}</td>
            <td>
              <span v-if="o.type === 'journey'" class="chip" :class="getStatusClass(o)">
                {{ getStatusText(o) }}
              </span>
              <div v-else>
                 <span class="chip" :class="o.orderPaid ? 'paid' : 'unpaid'">
                    {{ o.orderPaid ? 'Paid' : 'Unpaid' }}
                 </span>
                 <div v-if="o.status && o.status !== 'paid'" style="margin-top: 4px;">
                    <span class="chip" style="background: #e0e7ff; color: #3730a3;">
                        {{ o.status.charAt(0).toUpperCase() + o.status.slice(1) }}
                    </span>
                 </div>
              </div>
            </td>
            <td>
              <div v-if="o.type === 'journey'" class="progress-bar">
                <div class="progress-fill" :style="{ width: getProgress(o) + '%' }"></div>
                <span class="progress-text">{{ getProgressText(o) }}</span>
              </div>
              <span v-else>-</span>
            </td>
            <td>{{ o.total ? '$' + o.total : '-' }}</td>
            <td>{{ formatDate(o.timeOrdered || o.updatedAt) }}</td>
            <td><button @click="open(o)" class="view-btn">View</button></td>
          </tr>
        </tbody>
      </table>
      <div v-if="!filtered.length" class="empty">No orders</div>
    </div>

    <div v-if="selected" class="modal" @click.self="selected=null">
      <div class="modal-body">
        <div class="modal-header">
          <h3>
            <span v-if="selected.type === 'journey'">Journey</span>
            <span v-else>Order</span>
            {{ selected.id.substring(0, 16) }}...
          </h3>
          <button class="close" @click="selected=null">×</button>
        </div>
        
        <!-- Journey Progress Section -->
        <div v-if="selected.type === 'journey'" class="journey-progress-section">
          <h4>Journey Progress</h4>
          <div class="progress-details">
            <div class="progress-bar-large">
              <div class="progress-fill" :style="{ width: getProgress(selected) + '%' }"></div>
              <span class="progress-text-large">{{ getProgressText(selected) }}</span>
            </div>
            <div class="progress-info">
              <p><strong>Status:</strong> {{ getStatusText(selected) }}</p>
              <p><strong>Step:</strong> {{ selected.step || 0 }} of 3</p>
              <p><strong>Product:</strong> {{ selected.productName || 'N/A' }}</p>
              <p v-if="selected.profileType"><strong>Theme:</strong> {{ selected.profileType }}</p>
              <p v-if="selected.petName"><strong>Pet Name:</strong> {{ selected.petName }}</p>
              <p v-if="selected.petType"><strong>Pet Type:</strong> {{ selected.petType }}</p>
              <p v-if="selected.instagram"><strong>Instagram:</strong> @{{ selected.instagram }}</p>
              <p v-if="selected.comments"><strong>Comments:</strong> {{ selected.comments }}</p>
              <p v-if="selected.theme"><strong>Selected Theme:</strong> {{ selected.theme }}</p>
            </div>
          </div>
          
          <!-- Journey Images Section -->
          <div v-if="selected.imageUrls && selected.imageUrls.length > 0" class="journey-images-section">
            <h4>Uploaded Images ({{ selected.imageUrls.length }})</h4>
            <div class="journey-image-gallery">
              <div 
                v-for="(imgUrl, idx) in selected.imageUrls" 
                :key="idx" 
                class="journey-gallery-item"
                @click="openImageModal(imgUrl)"
              >
                <img :src="imgUrl" :alt="`Journey image ${idx + 1}`" />
                <div class="image-overlay">
                  <span>View Full Size</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Full Control Sets (if any) -->
          <div v-if="selected.fullControlSets && selected.fullControlSets.length > 0" class="control-sets-section">
            <h4>Customization Details</h4>
            <div class="control-sets">
              <div v-for="(set, idx) in selected.fullControlSets" :key="idx" class="control-set-detailed">
                <div class="control-set-header">
                  <h5>{{ set.name || `Set ${idx + 1}` }}</h5>
                  <p v-if="set.isComplete" class="set-complete">✓ Complete</p>
                  <p v-else class="set-incomplete">⏳ In Progress</p>
                </div>
                
                <!-- Show Professional/Show Component flags -->
                <div class="set-flags">
                  <span v-if="set.professional !== undefined" class="flag-badge">
                    Professional: {{ set.professional ? 'Yes' : 'No' }}
                  </span>
                  <span v-if="set.showComponent !== undefined" class="flag-badge">
                    Show Component: {{ set.showComponent ? 'Yes' : 'No' }}
                  </span>
                </div>
                
                <!-- NEW: Image Personalization Section -->
                <div v-if="set.personalization && set.personalization.length > 0" class="personalization-section">
                  <div v-for="(pEntry, pIdx) in set.personalization" :key="pIdx">
                    <!-- Image Personalization -->
                    <div v-if="pEntry.type === 'imagePersonalization' && pEntry.input && pEntry.input.length > 0" class="image-personalization-section">
                      <h6>📸 Image Personalization</h6>
                      
                      <div v-for="(inputItem, iIdx) in pEntry.input" :key="iIdx" class="personalization-input-item">
                        <!-- Uploaded Images -->
                        <div v-if="inputItem.type === 'uploaded'" class="uploaded-section">
                          <div class="input-type-badge uploaded">📤 Uploaded Images</div>
                          
                          <!-- Display Images -->
                          <div v-if="inputItem.urls && inputItem.urls.length > 0" class="personalization-image-grid">
                            <div 
                              v-for="(url, urlIdx) in inputItem.urls" 
                              :key="urlIdx"
                              class="personalization-image-item"
                              @click="openImageModal(url)"
                            >
                              <img :src="url" :alt="`Image ${urlIdx + 1}`" />
                              <div class="image-index-badge">{{ urlIdx }}</div>
                              <!-- Show pet name if assigned -->
                              <div v-if="getPetNameForImage(inputItem.multiplePetsList, urlIdx)" class="pet-name-overlay">
                                🐾 {{ getPetNameForImage(inputItem.multiplePetsList, urlIdx) }}
                              </div>
                            </div>
                          </div>
                          
                          <!-- Multiple Pets Info -->
                          <div v-if="inputItem.multiplePets && inputItem.multiplePetsList && inputItem.multiplePetsList.length > 0" class="pets-info-section">
                            <strong>🐾 Multiple Pets:</strong>
                            <div class="pets-detailed-list">
                              <div v-for="(pet, petIdx) in inputItem.multiplePetsList" :key="petIdx" class="pet-detail-card">
                                <div class="pet-name-header">
                                  <span class="pet-name">{{ pet.name }}</span>
                                  <span class="pet-species">{{ pet.species }}</span>
                                </div>
                                <div v-if="pet.imageIndices && pet.imageIndices.length > 0" class="pet-images-info">
                                  <strong>Images:</strong> 
                                  <span v-for="(imgIdx, i) in pet.imageIndices" :key="i" class="image-index-tag">
                                    #{{ imgIdx }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Portrait Style -->
                          <div v-if="inputItem.portraitStyle" class="portrait-style-badge">
                            🎨 Portrait Style Enabled
                          </div>
                        </div>
                        
                        <!-- Social Media -->
                        <div v-else-if="inputItem.type === 'social'" class="social-section">
                          <div class="input-type-badge social">📱 Social Media</div>
                          <div class="social-details">
                            <div><strong>Platform:</strong> {{ inputItem.platform }}</div>
                            <div><strong>Username:</strong> @{{ inputItem.username }}</div>
                          </div>
                          
                          <!-- Multiple Pets for Social -->
                          <div v-if="inputItem.multiplePets && inputItem.multiplePetsList && inputItem.multiplePetsList.length > 0" class="pets-info-section">
                            <strong>🐾 Multiple Pets:</strong>
                            <div class="pets-list-inline">
                              <span v-for="(pet, petIdx) in inputItem.multiplePetsList" :key="petIdx" class="pet-badge-inline">
                                {{ pet.name }} ({{ pet.species }})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Other Personalization Types (Color, Text, etc.) -->
                    <div v-else-if="pEntry.type === 'color'" class="other-personalization">
                      <strong>🎨 Color:</strong>
                      <span class="color-preview" :style="{ backgroundColor: pEntry.input }"></span>
                      <span>{{ pEntry.input }}</span>
                    </div>
                    <div v-else-if="pEntry.type === 'text'" class="other-personalization">
                      <strong>✏️ Text:</strong> {{ pEntry.input }}
                    </div>
                  </div>
                </div>
                
                <!-- LEGACY: Show User Selections (OLD DATA STRUCTURE) -->
                <div v-if="set.userSelections" class="user-selections-section legacy-section">
                  <h6>User Selections (Legacy):</h6>
                  
                  <!-- Selection Type -->
                  <div class="selection-item">
                    <strong>Type:</strong> 
                    <span class="selection-badge" :class="set.userSelections.type">
                      {{ set.userSelections.type === 'uploaded' ? 'Uploaded Images' : 'Social Media' }}
                    </span>
                  </div>
                  
                  <!-- User Uploaded Images -->
                  <div v-if="set.userSelections.type === 'uploaded' && set.userUploadedImageUrls && set.userUploadedImageUrls.length > 0" class="user-uploaded-images">
                    <strong>User Uploaded Images ({{ set.userUploadedImageUrls.length }}):</strong>
                    <div class="user-image-grid">
                      <div 
                        v-for="(url, idx) in set.userUploadedImageUrls" 
                        :key="idx"
                        class="user-image-item"
                        @click="openImageModal(url)"
                      >
                        <img :src="url" :alt="`User upload ${idx + 1}`" />
                        <div class="image-overlay">
                          <span>View Full Size</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Social Media Info -->
                  <div v-if="set.userSelections.type === 'social'" class="social-info">
                    <div class="selection-item">
                      <strong>Platform:</strong> {{ set.userSelections.platform }}
                    </div>
                    <div class="selection-item">
                      <strong>Username:</strong> @{{ set.userSelections.username }}
                    </div>
                  </div>
                  
                  <!-- Multiple Pets -->
                  <div v-if="set.userSelections.multiplePets" class="multiple-pets-info">
                    <div class="selection-item">
                      <strong>Multiple Pets:</strong> Yes
                    </div>
                    <div v-if="set.userSelections.multiplePetsList && set.userSelections.multiplePetsList.length > 0" class="pets-list">
                      <span 
                        v-for="(pet, idx) in set.userSelections.multiplePetsList" 
                        :key="idx" 
                        class="pet-badge"
                      >
                        {{ pet.name }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Portrait Style -->
                  <div v-if="set.userSelections.portraitStyle" class="selection-item">
                    <strong>Portrait Style:</strong> 
                    <span class="enabled-badge">Enabled</span>
                  </div>
                </div>
                
                <!-- Show Components (if available) -->
                <div v-if="set.components && set.components.length > 0" class="set-components">
                  <h6>Components Used:</h6>
                  <div class="components-list">
                    <span v-for="(comp, cIdx) in set.components" :key="cIdx" class="component-badge">
                      {{ typeof comp === 'string' ? comp : comp.id || comp }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Customer/User Info Section -->
        <div class="grid">
          <div v-if="selected.type === 'order'">
            <h4>Customer</h4>
            <div>{{ selected.name }}</div>
            <div>{{ selected.emailAddress }}</div>
            <div>{{ selected.accountEmail }}</div>
          </div>
          <div v-else>
            <h4>User</h4>
            <div>{{ selected.userEmail }}</div>
            <div>UID: {{ selected.userId }}</div>
          </div>
          
          <div v-if="selected.address">
            <h4>Address</h4>
            <div>{{ selected.address?.streetAddress }}</div>
            <div>{{ selected.address?.city }}, {{ selected.address?.state }}</div>
            <div>{{ selected.address?.country }}</div>
          </div>
          
          <div v-if="selected.type === 'order'">
            <h4>Payment & Status</h4>
            <div style="margin-bottom: 8px;">Payment: <span :class="selected.orderPaid ? 'paid' : 'unpaid'">{{ selected.orderPaid ? 'Paid' : 'Unpaid' }}</span></div>
            
            <div style="margin-bottom: 12px;">
                <label style="font-weight: 600; font-size: 13px; color: #666; margin-bottom: 4px; display: block;">Tracking Status:</label>
                <select v-model="selected.status" @change="updateOrderStatus(selected)" style="padding: 8px; border-radius: 6px; border: 1px solid #ddd; width: 100%; font-size: 14px;">
                    <option v-for="s in trackingStatuses" :key="s" :value="s">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</option>
                </select>
            </div>

            <div>Total: {{ selected.total }}</div>
            <div v-if="selected.timeOrdered">Created: {{ formatDate(selected.timeOrdered) }}</div>
          </div>
        </div>

        <!-- Order Items Section -->
        <div v-if="selected.type === 'order' && selected.order && selected.order.length > 0">
          <h4>Items</h4>
          <div class="items">
            <div class="item" v-for="(it, idx) in selected.order || []" :key="idx">
              <img v-if="it.images && it.images[0]" :src="it.images[0]" alt="" />
              <div class="info">
                <div class="title">{{ it.brand || it.name }} <span v-if="it.size">({{ it.size }})</span></div>
                <div class="meta">Qty: {{ it.quantity }} • Price: ${{ it.price }}</div>
                <div class="meta" v-if="it.petName">Pet: {{ it.petName }} <span v-if="it.petType">({{ it.petType }})</span></div>
                <div class="meta" v-if="it.profileType || it.profile">Theme: {{ it.profileType || it.profile }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="selected.uploadedImageUrls && selected.uploadedImageUrls.length > 0" class="uploaded-images-section">
          <h4>Customer Uploaded Images ({{ selected.uploadedImageUrls.length }})</h4>
          <div class="image-gallery">
            <div 
              v-for="(imgUrl, idx) in selected.uploadedImageUrls" 
              :key="idx" 
              class="gallery-item"
              @click="openImageModal(imgUrl)"
            >
              <img :src="imgUrl" :alt="`Uploaded image ${idx + 1}`" />
              <div class="image-overlay">
                <span>View Full Size</span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="imageModalUrl" class="image-modal" @click="imageModalUrl = null">
          <div class="image-modal-content" @click.stop>
            <button class="close-image" @click="imageModalUrl = null">×</button>
            <img :src="imageModalUrl" alt="Full size image" />
          </div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script>
import { db } from '@/firebase/init';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';

export default {
  name: 'OrdersPage',
  data() {
    return {
      loading: false,
      orders: [],
      journeys: [],
      search: '',
      statusFilter: 'all',
      selected: null,
      imageModalUrl: null,
      trackingStatuses: ['paid', 'design', 'production', 'shipped', 'warehouse', 'delivered', 'cancelled'],
      stats: {
        totalOrders: 0,
        inProgressJourneys: 0,
        completedOrders: 0,
        paidOrders: 0
      }
    };
  },
  computed: {
    allItems() {
      // Combine orders and journeys
      const orderItems = this.orders.map(o => ({ ...o, type: 'order' }));
      const journeyItems = this.journeys.map(j => ({ ...j, type: 'journey' }));
      return [...orderItems, ...journeyItems].sort((a, b) => {
        const aTime = a.timeOrderedTs || a.updatedAt || 0;
        const bTime = b.timeOrderedTs || b.updatedAt || 0;
        return bTime - aTime;
      });
    },
    filtered() {
      let items = this.allItems;
      
      // Apply status filter
      if (this.statusFilter !== 'all') {
        items = items.filter(item => {
          if (this.statusFilter === 'paid') {
            return item.type === 'order' && item.orderPaid;
          } else if (this.statusFilter === 'unpaid') {
            return item.type === 'order' && !item.orderPaid;
          } else if (this.statusFilter === 'in_progress') {
            return item.type === 'journey' && item.status === 'IN_PROGRESS';
          } else if (this.statusFilter === 'finished') {
            return item.type === 'journey' && item.status === 'FINISHED';
          }
          return true;
        });
      }
      
      // Apply search filter
      const s = (this.search || '').toLowerCase();
      if (s) {
        items = items.filter(o =>
          (o.accountEmail || '').toLowerCase().includes(s) ||
          (o.emailAddress || '').toLowerCase().includes(s) ||
          (o.userEmail || '').toLowerCase().includes(s) ||
          (o.name || '').toLowerCase().includes(s) ||
          (o.productName || '').toLowerCase().includes(s)
        );
      }
      
      return items;
    }
  },
  methods: {
    async fetchOrders() {
      try {
        const ref = collection(db, 'orders');
        let q;
        try {
          q = query(ref, orderBy('timeOrderedTs', 'desc'));
        } catch {
          q = query(ref, orderBy('timeOrdered', 'desc'));
        }
        const snap = await getDocs(q);
        this.orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error('Failed to load orders', e);
      }
    },
    async fetchJourneys() {
      try {
        const ref = collection(db, 'journeys');
        const snap = await getDocs(ref);
        this.journeys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error('Failed to load journeys', e);
      }
    },
    async fetchAllData() {
      this.loading = true;
      try {
        await Promise.all([this.fetchOrders(), this.fetchJourneys()]);
        this.calculateStats();
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        this.loading = false;
      }
    },
    calculateStats() {
      this.stats.totalOrders = this.orders.length;
      this.stats.paidOrders = this.orders.filter(o => o.orderPaid).length;
      // Completed = Finished journeys (not yet ordered)
      this.stats.completedOrders = this.journeys.filter(j => j.status === 'FINISHED' || j.isComplete).length;
      this.stats.inProgressJourneys = this.journeys.filter(j => j.status === 'IN_PROGRESS' && !j.isComplete).length;
    },
    getProgress(journey) {
      if (!journey || journey.type !== 'journey') return 0;
      if (journey.status === 'FINISHED' || journey.isComplete) return 100;
      const step = journey.step || 0;
      return Math.min((step / 3) * 100, 100);
    },
    getProgressText(journey) {
      if (!journey || journey.type !== 'journey') return '-';
      if (journey.status === 'FINISHED' || journey.isComplete) return 'Complete';
      const step = journey.step || 0;
      return `Step ${step}/3`;
    },
    getStatusText(item) {
      if (item.type === 'journey') {
        if (item.status === 'FINISHED' || item.isComplete) return 'Finished';
        return 'In Progress';
      }
      return item.orderPaid ? 'Paid' : 'Unpaid';
    },
    getStatusClass(item) {
      if (item.type === 'journey') {
        return item.status === 'FINISHED' || item.isComplete ? 'finished' : 'in-progress';
      }
      return item.orderPaid ? 'paid' : 'unpaid';
    },
    getRowClass(item) {
      if (item.type === 'journey') {
        return item.status === 'IN_PROGRESS' ? 'journey-row in-progress' : 'journey-row finished';
      }
      return 'order-row';
    },
    open(order) { this.selected = order; },
    openImageModal(url) {
      this.imageModalUrl = url;
    },
    formatDate(v) {
      if (!v) return '-';
      try {
        return new Date(v).toLocaleString();
      } catch { return String(v); }
    },
    getSetProperties(set) {
      // Extract all meaningful properties from the set, excluding system fields
      const excludeKeys = ['name', 'isComplete', 'id', 'personalization'];
      const properties = {};
      
      for (const [key, value] of Object.entries(set)) {
        if (!excludeKeys.includes(key) && value !== null && value !== undefined && value !== '') {
          // Skip empty arrays and objects
          if (Array.isArray(value) && value.length === 0) continue;
          if (typeof value === 'object' && Object.keys(value).length === 0) continue;
          
          properties[key] = value;
        }
      }
      
      return properties;
    },
    formatKey(key) {
      // Convert camelCase to Title Case
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    getPetNameForImage(multiplePetsList, imageIndex) {
      // Find which pet this image belongs to based on imageIndices
      if (!multiplePetsList || !Array.isArray(multiplePetsList)) return null;
      
      const pet = multiplePetsList.find(p => {
        return p.imageIndices && p.imageIndices.includes(imageIndex);
      });
      
      return pet ? pet.name : null;
    },
    async updateOrderStatus(order) {
        if (!order || !order.id) return;
        try {
            const orderRef = doc(db, 'orders', order.id);
            await updateDoc(orderRef, {
                status: order.status
            });
            console.log('Order status updated to', order.status);
        } catch (error) {
            console.error('Error updating order status:', error);
            alert('Failed to update status');
        }
    }
  },
  mounted() {
    this.fetchAllData();
  }
};
</script>

<style scoped>
.orders-page { padding: 20px; }

h2 {
  color: #e37267;
  margin-bottom: 20px;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-box {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
  border-left: 4px solid #e37267;
}

.stat-box h4 {
  margin: 0;
  font-size: 32px;
  color: #e37267;
  font-weight: 700;
}

.stat-box p {
  margin: 8px 0 0 0;
  color: #666;
  font-size: 14px;
}

/* Toolbar */
.toolbar { 
  display: flex; 
  gap: 12px; 
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.toolbar input { 
  padding: 10px 16px; 
  border: 1px solid #ddd; 
  border-radius: 8px; 
  flex: 1; 
  min-width: 250px;
  font-size: 14px;
}

.filter-select {
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  min-width: 200px;
}

.toolbar button { 
  padding: 10px 20px; 
  background: #e37267; 
  color: white; 
  border: none; 
  border-radius: 8px; 
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}

.toolbar button:hover { background: #d66259; }
.toolbar button:disabled { opacity: 0.5; cursor: not-allowed; }
.orders-table { width: 100%; border-collapse: collapse; background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden; }
.orders-table th { background: #fffef9; font-weight: 600; text-transform: uppercase; font-size: 12px; color: #666; }
.orders-table th, .orders-table td { border-bottom: 1px solid #eee; padding: 12px; text-align: left; }
.orders-table tbody tr:hover { background: #fafafa; }
.orders-table button { padding: 6px 12px; background: #e37267; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; }
.orders-table button:hover { background: #d66259; }
.chip { padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.paid { background: #e7f9ed; color: #137a2a; }
.unpaid { background: #fde8e8; color: #b42318; }
.in-progress { background: #fef3c7; color: #f59e0b; }
.finished { background: #dbeafe; color: #1e40af; }

/* Type Badges */
.id-code {
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 8px;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.type-badge.journey {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.type-badge.order {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e3a8a;
}

/* Progress Bar */
.progress-bar {
  width: 100%;
  height: 24px;
  background: #f0f0f0;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  transition: width 0.3s ease;
  border-radius: 12px;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 700;
  color: #333;
}

/* Row Styling */
.journey-row.in-progress {
  background: linear-gradient(90deg, #fffef9 0%, #fef3c7 100%);
}

.journey-row.finished {
  background: linear-gradient(90deg, #fffef9 0%, #dbeafe 100%);
}

.order-row {
  background: white;
}

.view-btn {
  padding: 6px 16px !important;
  background: #e37267 !important;
  color: white !important;
  border: none !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  font-weight: 600 !important;
  transition: all 0.2s !important;
}

.view-btn:hover {
  background: #d66259 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(227, 114, 103, 0.3);
}

.empty { padding: 40px; text-align: center; color: #888; background: white; border-radius: 8px; }
.modal { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: grid; place-items: center; z-index: 1000; overflow-y: auto; }
.modal-body { background: #fff; width: min(1000px, 92vw); max-height: 90vh; overflow-y: auto; border-radius: 12px; padding: 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); margin: 20px; }
.modal-header { display:flex; justify-content: space-between; align-items:center; margin-bottom: 16px; border-bottom: 2px solid #e37267; padding-bottom: 12px; }
.modal-header h3 { margin: 0; color: #e37267; }
.close { background: transparent; border: none; font-size: 32px; cursor: pointer; color: #999; line-height: 1; }
.close:hover { color: #e37267; }
.grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; margin-bottom: 20px; background: #fafafa; padding: 16px; border-radius: 8px; }
.grid h4 { margin: 0 0 8px 0; color: #e37267; font-size: 14px; text-transform: uppercase; }
.grid > div { display: flex; flex-direction: column; gap: 4px; }
.items { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
.items h4 { color: #e37267; margin-bottom: 12px; }
.item { display: grid; grid-template-columns: 80px 1fr; gap: 12px; padding: 12px; border: 1px solid #eee; border-radius: 8px; background: #fafafa; }
.item img { width: 80px; height: 80px; object-fit: cover; border-radius: 6px; }
.item .info { display: flex; flex-direction: column; gap: 4px; }
.title { font-weight: 600; font-size: 15px; color: #333; }
.meta { color: #666; font-size: 13px; }

/* Uploaded Images Gallery */
.uploaded-images-section { margin-top: 24px; padding-top: 24px; border-top: 2px solid #e37267; }
.uploaded-images-section h4 { color: #e37267; margin-bottom: 16px; font-size: 16px; }
.image-gallery { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; }
.gallery-item { position: relative; aspect-ratio: 1; border-radius: 8px; overflow: hidden; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: transform 0.2s, box-shadow 0.2s; }
.gallery-item:hover { transform: translateY(-4px); box-shadow: 0 4px 16px rgba(227, 114, 103, 0.3); }
.gallery-item img { width: 100%; height: 100%; object-fit: cover; }
.image-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.6); display: grid; place-items: center; opacity: 0; transition: opacity 0.2s; color: white; font-size: 13px; font-weight: 500; }
.gallery-item:hover .image-overlay { opacity: 1; }

/* Full Size Image Modal */
.image-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.9); display: grid; place-items: center; z-index: 2000; cursor: pointer; }
.image-modal-content { position: relative; max-width: 90vw; max-height: 90vh; cursor: default; }
.image-modal-content img { max-width: 100%; max-height: 90vh; border-radius: 8px; box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
.close-image { position: absolute; top: -40px; right: 0; background: white; border: none; width: 36px; height: 36px; border-radius: 50%; font-size: 24px; cursor: pointer; color: #333; display: grid; place-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); }
.close-image:hover { background: #e37267; color: white; }

/* Journey Progress Section in Modal */
.journey-progress-section {
  background: linear-gradient(135deg, #fef3c7 0%, #fffef9 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.journey-progress-section h4 {
  margin: 0 0 16px 0;
  color: #f59e0b;
  font-size: 18px;
}

.progress-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.progress-bar-large {
  width: 100%;
  height: 40px;
  background: #fff;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
}

.progress-bar-large .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  transition: width 0.3s ease;
}

.progress-text-large {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.progress-info {
  background: white;
  padding: 16px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.progress-info p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.progress-info strong {
  color: #333;
  font-weight: 600;
}

/* Journey Images Section */
.journey-images-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f59e0b;
}

.journey-images-section h4 {
  margin: 0 0 16px 0;
  color: #f59e0b;
  font-size: 16px;
}

.journey-image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.journey-gallery-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  background: white;
}

.journey-gallery-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.3);
}

.journey-gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Control Sets Section */
.control-sets-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f59e0b;
}

.control-sets-section h4 {
  margin: 0 0 16px 0;
  color: #f59e0b;
  font-size: 16px;
}

.control-sets {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.control-set {
  background: white;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
}

.control-set-detailed {
  background: white;
  padding: 20px;
  border-radius: 12px;
  border-left: 4px solid #f59e0b;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.control-set-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #fef3c7;
}

.control-set h5,
.control-set-header h5 {
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 700;
}

.set-complete {
  margin: 0;
  color: #10b981;
  font-weight: 600;
  font-size: 13px;
  background: #e7f9ed;
  padding: 4px 12px;
  border-radius: 12px;
}

.set-incomplete {
  margin: 0;
  color: #f59e0b;
  font-weight: 600;
  font-size: 13px;
  background: #fef3c7;
  padding: 4px 12px;
  border-radius: 12px;
}

/* Personalization Details */
.set-personalization,
.set-properties {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.personalization-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #fafafa;
  border-radius: 6px;
  font-size: 14px;
}

.personalization-item strong {
  color: #666;
  min-width: 100px;
  font-weight: 600;
}

.color-preview {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid #ddd;
  vertical-align: middle;
  margin: 0 4px;
}

/* Set Flags */
.set-flags {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.flag-badge {
  padding: 4px 12px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* User Selections Section */
.user-selections-section {
  margin-top: 16px;
  background: #f0f9ff;
  padding: 16px;
  border-radius: 8px;
}

.user-selections-section h6 {
  margin: 0 0 16px 0;
  color: #e37267;
  font-size: 15px;
  font-weight: 700;
}

.selection-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.selection-badge.uploaded {
  background: #dbeafe;
  color: #1e40af;
}

.selection-badge.social {
  background: #fce7f3;
  color: #be123c;
}

.enabled-badge {
  padding: 4px 12px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* User Uploaded Images */
.user-uploaded-images {
  margin-top: 16px;
}

.user-uploaded-images strong {
  display: block;
  margin-bottom: 12px;
  color: #111827;
  font-size: 14px;
}

.user-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.user-image-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.user-image-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(227, 114, 103, 0.3);
}

.user-image-item:hover .image-overlay {
  opacity: 1;
}

.user-image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: grid;
  place-items: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-overlay span {
  color: white;
  font-size: 12px;
  font-weight: 600;
}

/* Social Info */
.social-info {
  margin-top: 12px;
}

/* Multiple Pets */
.multiple-pets-info {
  margin-top: 12px;
}

.pets-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.pet-badge {
  padding: 6px 12px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Props (User Selections) */
.set-props {
  margin-top: 16px;
}

.set-props h6 {
  margin: 0 0 12px 0;
  color: #e37267;
  font-size: 14px;
  font-weight: 700;
}

.prop-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.prop-header strong {
  color: #111827;
  font-size: 15px;
  display: block;
  margin-bottom: 12px;
}

.prop-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.prop-image-item {
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
}

.prop-image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.prop-selection {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.selection-label {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
}

.selection-value {
  color: #111827;
  font-size: 13px;
  background: white;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.prop-colors {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.color-swatches {
  display: flex;
  gap: 6px;
}

.color-swatch {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: transform 0.2s;
}

.color-swatch:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* Components */
.set-components {
  margin-top: 16px;
}

.set-components h6 {
  margin: 0 0 8px 0;
  color: #e37267;
  font-size: 14px;
  font-weight: 700;
}

.components-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.component-badge {
  padding: 6px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Current Component */
.current-component {
  margin-top: 12px;
  padding: 10px;
  background: #fef3c7;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.component-label {
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
}

.component-value {
  color: #78350f;
  font-size: 13px;
  font-weight: 700;
}

/* Emits (Selected Items) */
.set-emits {
  margin-top: 16px;
}

.set-emits h6 {
  margin: 0 0 8px 0;
  color: #e37267;
  font-size: 14px;
  font-weight: 700;
}

.emits-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.emit-item {
  padding: 8px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
}

/* NEW: Image Personalization Sections */
.personalization-section {
  margin-top: 20px;
}

.image-personalization-section {
  background: #f0f9ff;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.image-personalization-section h6 {
  margin: 0 0 16px 0;
  color: #0369a1;
  font-size: 15px;
  font-weight: 700;
}

.personalization-input-item {
  margin-bottom: 20px;
}

.input-type-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
}

.input-type-badge.uploaded {
  background: #dbeafe;
  color: #0369a1;
}

.input-type-badge.social {
  background: #fce7f3;
  color: #be185d;
}

.personalization-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.personalization-image-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #e5e7eb;
  transition: transform 0.2s, box-shadow 0.2s;
}

.personalization-image-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.personalization-image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-index-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.pet-name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
  padding: 8px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.pets-info-section {
  margin-top: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
}

.pets-info-section strong {
  display: block;
  margin-bottom: 12px;
  color: #92400e;
  font-size: 14px;
}

.pets-detailed-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.pet-detail-card {
  background: #fef3c7;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #fbbf24;
}

.pet-name-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.pet-name {
  font-weight: 700;
  color: #92400e;
  font-size: 14px;
}

.pet-species {
  font-size: 12px;
  color: #78350f;
  background: white;
  padding: 2px 8px;
  border-radius: 10px;
}

.pet-images-info {
  margin-top: 8px;
  font-size: 12px;
  color: #78350f;
}

.pet-images-info strong {
  display: inline;
  margin: 0;
  font-size: 12px;
}

.image-index-tag {
  display: inline-block;
  background: white;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 4px;
}

.portrait-style-badge {
  display: inline-block;
  margin-top: 12px;
  padding: 6px 12px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.social-section {
  background: #fef2f2;
  padding: 12px;
  border-radius: 8px;
}

.social-details {
  margin-top: 8px;
}

.social-details div {
  margin-bottom: 6px;
  font-size: 13px;
  color: #374151;
}

.pets-list-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.pet-badge-inline {
  padding: 4px 10px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.other-personalization {
  padding: 10px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #374151;
}

.legacy-section {
  opacity: 0.7;
  border-top: 2px dashed #e5e7eb;
  padding-top: 16px;
  margin-top: 16px;
}

.legacy-section h6::after {
  content: " (Old Format)";
  font-size: 11px;
  color: #9ca3af;
  font-weight: 400;
}
</style>